import { useMemo, useState } from 'react';
import { MdAdd } from 'react-icons/md';

import { Button } from '@/components/Button';
import Loading from '@/components/Loading';
import type AgentApiCandidateMailTemplatesRecord from '@/types/serializers/AgentApiCandidateMailTemplatesRecord';
import { CANDIDATE_MAIL_PAGE_SIZE } from '../constants';
import useClientPagination from '../hooks/useClientPagination';
import { matchesKeyword, toBodyPreview } from '../lib/mailFolders';
import { formatListDate } from '../lib/scheduledAt';
import MailListRow from './MailListRow';
import MailListToolbar from './MailListToolbar';

type CandidateMailTemplateListProps = {
  templates: AgentApiCandidateMailTemplatesRecord[];
  isLoading: boolean;
  isError: boolean;
  onCreate: () => void;
  onOpenTemplate: (templateId: number) => void;
};

export default function CandidateMailTemplateList({
  templates,
  isLoading,
  isError,
  onCreate,
  onOpenTemplate,
}: CandidateMailTemplateListProps) {
  const [keyword, setKeyword] = useState('');
  const filteredTemplates = useMemo(
    () =>
      templates.filter((template) =>
        matchesKeyword([template.name, template.subject, template.body], keyword)
      ),
    [keyword, templates]
  );
  const { pagedItems, rangeLabel, canGoPrev, canGoNext, goPrev, goNext } = useClientPagination(
    filteredTemplates,
    CANDIDATE_MAIL_PAGE_SIZE,
    keyword
  );

  return (
    <div className="flex w-full flex-col">
      <MailListToolbar
        keyword={keyword}
        onKeywordChange={setKeyword}
        placeholder="テンプレート名・件名・本文で検索"
        rangeLabel={rangeLabel}
        canGoPrev={canGoPrev}
        canGoNext={canGoNext}
        onPrev={goPrev}
        onNext={goNext}
        action={
          <Button type="button" color="primary" size="md" onClick={onCreate}>
            <MdAdd className="size-5" />
            テンプレート作成
          </Button>
        }
      />
      {isLoading ? (
        <div className="flex justify-center py-24">
          <Loading size="small" />
        </div>
      ) : isError ? (
        <p className="px-lg py-xl text-center text-body-sm text-sub">
          テンプレートの取得に失敗しました。再度お試しください。
        </p>
      ) : pagedItems.length === 0 ? (
        <p className="px-lg py-xl text-center text-body-sm text-sub">
          {templates.length === 0 ? 'テンプレートはありません' : '該当するテンプレートはありません'}
        </p>
      ) : (
        <ul className="flex flex-col">
          {pagedItems.map((template) => (
            <MailListRow
              key={template.id}
              subject={template.name}
              snippet={[template.subject, toBodyPreview(template.body)].filter(Boolean).join(' — ')}
              author={template.creator_name ?? '-'}
              date={formatListDate(new Date(template.updated_at))}
              onClick={() => onOpenTemplate(template.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
